import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { useDispatch, useSelector } from 'react-redux';

import Transitions from '../transitions';
import ROUTE_NAMES from 'routes/routeNames';
import { ReducersModal } from 'utils/modals';
import { logoutManually } from 'modules/auth/action';
import { getUserRoleLabel, USER_ROLE } from 'utils/userType';
import { BRAND } from 'themes';
import HelpSupportModal from './helpSupportModal';

const initials = (name: string) => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

function AccountAvatar({
    name,
    photo,
    size = 32,
}: {
    name: string;
    photo?: string;
    size?: number;
}) {
    if (photo) {
        return (
            <Avatar
                alt={name}
                src={photo}
                sx={{
                    width: size,
                    height: size,
                    flexShrink: 0,
                    boxShadow: '0 0 0 3px #EDE9FE',
                }}
            />
        );
    }

    return (
        <Box
            aria-hidden
            sx={{
                width: size,
                height: size,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                color: '#fff',
                fontWeight: 700,
                fontSize: size > 34 ? 14 : 12,
                lineHeight: 1,
                bgcolor: BRAND.primary,
                boxShadow: '0 0 0 3px #EDE9FE',
            }}>
            {initials(name)}
        </Box>
    );
}

const menuItemSx = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    height: 44,
    px: 1.25,
    border: 0,
    borderRadius: '12px',
    bgcolor: BRAND.track,
    color: BRAND.ink,
    font: 'inherit',
    fontWeight: 600,
    fontSize: 14,
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'background-color 200ms ease, color 200ms ease',
    '&:focus-visible': {
        outline: `2px solid ${BRAND.primary}`,
        outlineOffset: 2,
    },
} as const;

const ProfileSection = () => {
    const navigate = useNavigate();
    const dispatch: any = useDispatch();
    const [open, setOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const anchorRef: any = useRef(null);
    const prevOpen = useRef(open);

    const { firstName, lastName, role, profilePhoto } = useSelector(
        (state: ReducersModal) => state.AuthReducer
    );

    const displayName = `${firstName || ''} ${lastName || ''}`.trim() || 'User';
    const roleLabel = getUserRoleLabel(role as string);
    const isAdmin = role === USER_ROLE.SUPER_ADMIN;

    const handleClose = (event: any) => {
        if (anchorRef.current && anchorRef.current?.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleOpenHelp = () => {
        setOpen(false);
        setHelpOpen(true);
    };

    const handleLogOut = () => {
        setOpen(false);
        dispatch(
            logoutManually(() => {
                sessionStorage.clear();
                navigate(ROUTE_NAMES.BASE_ROUTE);
            })
        );
    };

    useEffect(() => {
        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Box
                component='button'
                type='button'
                ref={anchorRef}
                onClick={() => setOpen((prev) => !prev)}
                aria-label='account menu'
                aria-controls={open ? 'profile-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup='menu'
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    height: 44,
                    m: 0,
                    pl: 0.75,
                    pr: 1,
                    borderRadius: '999px',
                    border: '1px solid transparent',
                    bgcolor: open ? '#F4F2FB' : 'transparent',
                    color: BRAND.ink,
                    font: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                    WebkitAppearance: 'none',
                    transition: 'background-color 200ms ease',
                    '&:hover': {
                        bgcolor: '#F4F2FB',
                    },
                    '&:focus-visible': {
                        outline: `2px solid ${BRAND.primary}`,
                        outlineOffset: 2,
                    },
                }}>
                <AccountAvatar name={displayName} photo={profilePhoto} />
                <Box
                    sx={{
                        display: { xs: 'none', sm: 'flex' },
                        flexDirection: 'column',
                        justifyContent: 'center',
                        minWidth: 0,
                    }}>
                    <Typography
                        component='span'
                        noWrap
                        sx={{
                            maxWidth: 148,
                            fontWeight: 600,
                            fontSize: 14,
                            lineHeight: 1.15,
                            letterSpacing: '-0.01em',
                            color: BRAND.ink,
                        }}>
                        {displayName}
                    </Typography>
                    <Typography
                        component='span'
                        noWrap
                        sx={{
                            maxWidth: 148,
                            mt: '2px',
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: 1.15,
                            color: '#5C5870',
                        }}>
                        {roleLabel}
                    </Typography>
                </Box>
                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: 18,
                        color: '#5C5870',
                        transform: open ? 'rotate(180deg)' : 'none',
                        transition: 'transform 200ms ease',
                        '@media (prefers-reduced-motion: reduce)': {
                            transition: 'none',
                        },
                    }}
                />
            </Box>

            <Popper
                placement='bottom-end'
                open={open}
                anchorEl={anchorRef.current}
                transition
                sx={{ zIndex: 1400 }}
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 8],
                            },
                        },
                    ],
                }}>
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps} position='top-right'>
                        <Paper
                            id='profile-menu'
                            elevation={0}
                            role='menu'
                            aria-label='Account'
                            sx={{
                                minWidth: 180,
                                borderRadius: '14px',
                                overflow: 'hidden',
                                border: `1px solid ${BRAND.border}`,
                                bgcolor: '#fff',
                                boxShadow: '0 18px 40px -16px rgba(27, 19, 64, 0.28)',
                            }}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <Box>
                                    <Box
                                        sx={{
                                            display: { xs: 'flex', sm: 'none' },
                                            alignItems: 'center',
                                            gap: 1.5,
                                            px: 1.5,
                                            py: 1.5,
                                            borderBottom: `1px solid ${BRAND.border}`,
                                        }}>
                                        <AccountAvatar
                                            name={displayName}
                                            photo={profilePhoto}
                                            size={36}
                                        />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                noWrap
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    lineHeight: 1.2,
                                                    letterSpacing: '-0.02em',
                                                    color: BRAND.ink,
                                                }}>
                                                {displayName}
                                            </Typography>
                                            <Typography
                                                noWrap
                                                sx={{
                                                    mt: 0.25,
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    lineHeight: 1.2,
                                                    color: '#5C5870',
                                                }}>
                                                {roleLabel}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            p: 0.75,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.75,
                                        }}>
                                        {isAdmin ? (
                                            <Box
                                                component='button'
                                                type='button'
                                                role='menuitem'
                                                onClick={handleOpenHelp}
                                                sx={{
                                                    ...menuItemSx,
                                                    '&:hover': {
                                                        bgcolor: '#EDE9FE',
                                                        color: BRAND.primaryDark,
                                                        '& .help-icon': {
                                                            color: BRAND.primaryDark,
                                                            bgcolor: '#fff',
                                                        },
                                                    },
                                                }}>
                                                <Box
                                                    className='help-icon'
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        borderRadius: '8px',
                                                        display: 'grid',
                                                        placeItems: 'center',
                                                        bgcolor: '#fff',
                                                        color: BRAND.primary,
                                                        flexShrink: 0,
                                                        transition:
                                                            'background-color 200ms ease, color 200ms ease',
                                                    }}>
                                                    <SupportAgentOutlinedIcon sx={{ fontSize: 16 }} />
                                                </Box>
                                                Help & Support
                                            </Box>
                                        ) : null}

                                        <Box
                                            component='button'
                                            type='button'
                                            role='menuitem'
                                            onClick={handleLogOut}
                                            sx={{
                                                ...menuItemSx,
                                                '&:hover': {
                                                    bgcolor: '#FDECEC',
                                                    color: '#B42318',
                                                    '& .logout-icon': {
                                                        color: '#B42318',
                                                        bgcolor: '#fff',
                                                    },
                                                },
                                            }}>
                                            <Box
                                                className='logout-icon'
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: '8px',
                                                    display: 'grid',
                                                    placeItems: 'center',
                                                    bgcolor: '#fff',
                                                    color: BRAND.primary,
                                                    flexShrink: 0,
                                                    transition:
                                                        'background-color 200ms ease, color 200ms ease',
                                                }}>
                                                <LogoutOutlinedIcon sx={{ fontSize: 16 }} />
                                            </Box>
                                            Log out
                                        </Box>
                                    </Box>
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>

            {isAdmin ? (
                <HelpSupportModal open={helpOpen} onClose={() => setHelpOpen(false)} />
            ) : null}
        </>
    );
};

export default ProfileSection;
